// src/features/Planning/components/Step7MarkSetbacks.jsx
import {
  Box,
  Flex,
  Text,
  Button,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from "@chakra-ui/react";
import { useEffect, useRef, useState, useCallback } from "react";

export default function Step7MarkSetbacks({
  formData,
  setFormData,
  onNext,
  onSubmit,
  onBack,
  isLastStep,
  viewMode,
}) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const scaleRef = useRef({ shiftX: 0, shiftY: 0, finalScale: 1 });

  const [modelPoints, setModelPoints] = useState(null);
  const [bounding, setBounding] = useState(null);
  const [buildingPoints, setBuildingPoints] = useState([]);
  const [svgDimensions, setSvgDimensions] = useState({ width: 400, height: 380 });

  // Track if maximize has been auto-applied on first load
  const maximizeAppliedRef = useRef(false);

  const dragRef = useRef({
    active: false,
    type: null,
    pointIndex: -1,
    offsetX: 0,
    offsetY: 0,
  });

  const buildingPointsRef = useRef(buildingPoints);
  const modelPointsRef = useRef(modelPoints);
  useEffect(() => { buildingPointsRef.current = buildingPoints; }, [buildingPoints]);
  useEffect(() => { modelPointsRef.current = modelPoints; }, [modelPoints]);

  // ── Container resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        setSvgDimensions({
          width: containerRef.current.clientWidth - 8,
          height: containerRef.current.clientHeight - 8,
        });
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const snapToGrid = (v, g = 0.1) => Math.round(v / g) * g;

  const calculateDistance = (p1, p2) =>
    Math.round(Math.hypot(p2.x - p1.x, p2.y - p1.y) * 10) / 10;

  const convertToFeetInches = (totalFeet) => {
    const totalInches = Math.round(totalFeet * 12);
    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;
    return { feet, inches };
  };

  const toFeetDotInches = (totalFeet) => {
    const { feet, inches } = convertToFeetInches(totalFeet);
    return inches === 0 ? String(feet) : `${feet}.${inches}`;
  };

  const formatDistance = (distance) => {
    const { feet, inches } = convertToFeetInches(distance);
    return inches === 0 ? `${feet}'` : `${feet}'${inches}"`;
  };

  const pointInPolygon = useCallback((point, vs) => {
    let inside = false;
    for (let i = 0, j = vs.length - 1; i < vs.length; j = i++) {
      const xi = vs[i].x, yi = vs[i].y;
      const xj = vs[j].x, yj = vs[j].y;
      if ((yi > point.y) !== (yj > point.y) &&
        point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  const isPointValidWith = useCallback((point, mp) => {
    if (!mp) return false;
    const eps = 0.5;
    const expandedPoly = [
      { x: mp.tl.x - eps, y: mp.tl.y - eps },
      { x: mp.tr.x + eps, y: mp.tr.y - eps },
      { x: mp.br.x + eps, y: mp.br.y + eps },
      { x: mp.bl.x - eps, y: mp.bl.y + eps },
    ];
    return pointInPolygon(point, expandedPoly);
  }, [pointInPolygon]);

  // ── Model points from formData ────────────────────────────────────────────────
  useEffect(() => {
    const toFt = (f, i) => (parseFloat(f) || 0) + (parseFloat(i) || 0) / 12;

    let fw, bw, ld, rd;
    if (formData.shape === "rectangular") {
      fw = toFt(formData.size?.widthFeet, formData.size?.widthInches);
      bw = fw;
      ld = toFt(formData.size?.depthFeet, formData.size?.depthInches);
      rd = ld;
    } else if (formData.size?.frontFeet !== undefined) {
      fw = toFt(formData.size.frontFeet, formData.size.frontInches);
      bw = toFt(formData.size.backFeet, formData.size.backInches);
      ld = toFt(formData.size.leftFeet, formData.size.leftInches);
      rd = toFt(formData.size.rightFeet, formData.size.rightInches);
    } else {
      fw = parseFloat(formData.size?.front) || 0;
      bw = parseFloat(formData.size?.back) || 0;
      ld = parseFloat(formData.size?.left) || 0;
      rd = parseFloat(formData.size?.right) || 0;
    }
    if (fw <= 0 || bw <= 0 || ld <= 0 || rd <= 0) return;

    const diff = bw - fw;
    let a, h;
    if (Math.abs(diff) < 0.01) {
      a = 0; h = (ld + rd) / 2;
    } else {
      const avg = (ld + rd) / 2;
      if (Math.abs(diff) < Math.abs(ld - rd) || Math.abs(diff) > ld + rd) {
        h = avg * 0.8; a = diff / 2;
      } else {
        a = (rd * rd - ld * ld - diff * diff) / (2 * diff);
        const h2 = ld * ld - a * a;
        if (h2 > 0) { h = Math.sqrt(h2); } else { h = avg * 0.8; a = diff / 2; }
      }
    }
    h = Math.max(h, 1);

    const pts = {
      tl: { x: a, y: 0 },
      tr: { x: a + bw, y: 0 },
      bl: { x: 0, y: h },
      br: { x: fw, y: h },
    };

    const minX = Math.min(pts.tl.x, pts.tr.x, pts.bl.x, pts.br.x);
    const minY = Math.min(pts.tl.y, pts.tr.y, pts.bl.y, pts.br.y);
    const maxX = Math.max(pts.tl.x, pts.tr.x, pts.bl.x, pts.br.x);
    const maxY = Math.max(pts.tl.y, pts.tr.y, pts.bl.y, pts.br.y);
    const nb = { minX, minY, maxX, maxY };

    setBounding(nb);
    setModelPoints(pts);

    // Restore saved buildingArea if valid
    const saved = formData.buildingArea;
    if (Array.isArray(saved) && saved.length === 4 &&
      saved.every((p) => typeof p.x === "number" && typeof p.y === "number")) {
      const eps = 0.5;
      const expandedPoly = [
        { x: pts.tl.x - eps, y: pts.tl.y - eps },
        { x: pts.tr.x + eps, y: pts.tr.y - eps },
        { x: pts.br.x + eps, y: pts.br.y + eps },
        { x: pts.bl.x - eps, y: pts.bl.y + eps },
      ];
      if (saved.every((p) => pointInPolygon(p, expandedPoly))) {
        setBuildingPoints(saved);
        return;
      }
    }

    // DEFAULT: Maximize Area — building points = plot boundary points
    const maximizedPts = [
      { x: pts.tl.x, y: pts.tl.y },
      { x: pts.tr.x, y: pts.tr.y },
      { x: pts.br.x, y: pts.br.y },
      { x: pts.bl.x, y: pts.bl.y },
    ];
    setBuildingPoints(maximizedPts);
    maximizeAppliedRef.current = true;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.shape,
    formData.size?.widthFeet, formData.size?.widthInches,
    formData.size?.depthFeet, formData.size?.depthInches,
    formData.size?.frontFeet, formData.size?.frontInches,
    formData.size?.backFeet, formData.size?.backInches,
    formData.size?.leftFeet, formData.size?.leftInches,
    formData.size?.rightFeet, formData.size?.rightInches,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formData.buildingArea),
    pointInPolygon,
  ]);

  // ── Coord helpers ─────────────────────────────────────────────────────────────
  const getModelCoords = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const { shiftX, shiftY, finalScale } = scaleRef.current;
    return {
      x: (clientX - rect.left - shiftX) / finalScale,
      y: (clientY - rect.top - shiftY) / finalScale,
    };
  };

  // ── STABLE event handlers ────────────────────────────────────────────────────
  const onMouseDown = useCallback((pointIndex, type, e, isTouch = false) => {
    if (isTouch) e.preventDefault();
    e.stopPropagation();

    const pts = buildingPointsRef.current;
    if (!pts || !pts.length) return;

    const { clientX, clientY } = isTouch
      ? (e.touches?.[0] || e.changedTouches?.[0] || {})
      : e;

    const model = getModelCoords(clientX, clientY);

    let ox, oy;
    if (type === "corner") {
      ox = model.x - pts[pointIndex].x;
      oy = model.y - pts[pointIndex].y;
    } else {
      const p2 = (pointIndex + 1) % 4;
      ox = model.x - (pts[pointIndex].x + pts[p2].x) / 2;
      oy = model.y - (pts[pointIndex].y + pts[p2].y) / 2;
    }

    dragRef.current = { active: true, type, pointIndex, offsetX: ox, offsetY: oy };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMouseMove = useCallback((e, isTouch = false) => {
    const drag = dragRef.current;
    if (!drag.active) return;
    if (isTouch) e.preventDefault();

    const { clientX, clientY } = isTouch
      ? (e.touches?.[0] || e.changedTouches?.[0] || {})
      : e;

    const model = getModelCoords(clientX, clientY);
    const mp = modelPointsRef.current;
    const pts = [...buildingPointsRef.current];

    if (drag.type === "corner") {
      const nx = snapToGrid(model.x - drag.offsetX);
      const ny = snapToGrid(model.y - drag.offsetY);
      const np = { x: nx, y: ny };
      if (isPointValidWith(np, mp)) {
        pts[drag.pointIndex] = np;
        setBuildingPoints(pts);
      }
    } else {
      const i1 = drag.pointIndex;
      const i2 = (i1 + 1) % 4;
      const midX = (pts[i1].x + pts[i2].x) / 2;
      const midY = (pts[i1].y + pts[i2].y) / 2;
      const tx = model.x - drag.offsetX;
      const ty = model.y - drag.offsetY;

      let dx = 0, dy = 0;
      if (i1 === 0 || i1 === 2) dy = snapToGrid(ty - midY);
      else dx = snapToGrid(tx - midX);

      const np1 = { x: pts[i1].x + dx, y: pts[i1].y + dy };
      const np2 = { x: pts[i2].x + dx, y: pts[i2].y + dy };
      if (isPointValidWith(np1, mp) && isPointValidWith(np2, mp)) {
        pts[i1] = np1;
        pts[i2] = np2;
        setBuildingPoints(pts);
      }
    }
  }, [isPointValidWith]);

  const onMouseUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  useEffect(() => {
    const mm = (e) => onMouseMove(e);
    const mu = () => onMouseUp();
    const tm = (e) => onMouseMove(e, true);
    const tu = () => onMouseUp();

    document.addEventListener("mousemove", mm);
    document.addEventListener("mouseup", mu);
    document.addEventListener("touchmove", tm, { passive: false });
    document.addEventListener("touchend", tu);

    return () => {
      document.removeEventListener("mousemove", mm);
      document.removeEventListener("mouseup", mu);
      document.removeEventListener("touchmove", tm);
      document.removeEventListener("touchend", tu);
    };
  }, [onMouseMove, onMouseUp]);

  // ── distPointToSegment ────────────────────────────────────────────────────────
  const distPointToSegment = (p, a, b) => {
    const dx = b.x - a.x, dy = b.y - a.y;
    if (!dx && !dy) return { dist: Math.hypot(p.x - a.x, p.y - a.y), closest: a };
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
    t = Math.max(0, Math.min(1, t));
    const proj = { x: a.x + t * dx, y: a.y + t * dy };
    return { dist: Math.hypot(p.x - proj.x, p.y - proj.y), closest: proj };
  };

  // ── calculateGaps ─────────────────────────────────────────────────────────────
  const calculateGaps = useCallback(() => {
    if (!modelPoints || buildingPoints.length === 0) return [];

    const mp = modelPoints;
    const pts = buildingPoints;

    const segBack = [mp.tl, mp.tr];
    const segFront = [mp.br, mp.bl];
    const segRight = [mp.tr, mp.br];
    const segLeft = [mp.bl, mp.tl];

    const midBack = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    const midRight = { x: (pts[1].x + pts[2].x) / 2, y: (pts[1].y + pts[2].y) / 2 };
    const midFront = { x: (pts[2].x + pts[3].x) / 2, y: (pts[2].y + pts[3].y) / 2 };
    const midLeft = { x: (pts[3].x + pts[0].x) / 2, y: (pts[3].y + pts[0].y) / 2 };

    const distToSeg = (p, seg) => distPointToSegment(p, seg[0], seg[1]);

    const cornerGap = (p, seg1, seg2) => {
      const r1 = distToSeg(p, seg1);
      const r2 = distToSeg(p, seg2);
      return r1.dist <= r2.dist ? r1 : r2;
    };

    const gapDefs = [
      { label: "1 (Back-Left)", point: pts[0], calc: () => cornerGap(pts[0], segBack, segLeft) },
      { label: "2 (Back-Right)", point: pts[1], calc: () => cornerGap(pts[1], segBack, segRight) },
      { label: "3 (Front-Right)", point: pts[2], calc: () => cornerGap(pts[2], segFront, segRight) },
      { label: "4 (Front-Left)", point: pts[3], calc: () => cornerGap(pts[3], segFront, segLeft) },
      { label: "5 (Mid-Back)", point: midBack, calc: () => distToSeg(midBack, segBack) },
      { label: "6 (Mid-Right)", point: midRight, calc: () => distToSeg(midRight, segRight) },
      { label: "7 (Mid-Front)", point: midFront, calc: () => distToSeg(midFront, segFront) },
      { label: "8 (Mid-Left)", point: midLeft, calc: () => distToSeg(midLeft, segLeft) },
    ];

    return gapDefs.map(({ label, point, calc }) => {
      const result = calc();
      return {
        label,
        gap: Math.max(0, Math.round(result.dist * 10) / 10),
        closestPoint: result.closest,
        point,
      };
    });
  }, [modelPoints, buildingPoints]);

  const calculateDiagonals = useCallback(() => {
    if (buildingPoints.length !== 4) return { diagonal1: 0, diagonal2: 0 };
    return {
      diagonal1: Math.round(Math.hypot(
        buildingPoints[2].x - buildingPoints[0].x,
        buildingPoints[2].y - buildingPoints[0].y) * 10) / 10,
      diagonal2: Math.round(Math.hypot(
        buildingPoints[3].x - buildingPoints[1].x,
        buildingPoints[3].y - buildingPoints[1].y) * 10) / 10,
    };
  }, [buildingPoints]);

  // ── Area Calculation ──────────────────────────────────────────────────────────
  const calcPolygonArea = (pts) => {
    if (!pts || pts.length < 3) return 0;
    let area = 0;
    for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
      area += (pts[j].x + pts[i].x) * (pts[j].y - pts[i].y);
    }
    return Math.abs(area / 2);
  };

  const exactToFt = (f, i) => (parseFloat(f) || 0) + (parseFloat(i) || 0) / 12;

  const plotArea = (() => {
    if (!formData.size) return 0;
    if (formData.shape === "rectangular") {
      const w = exactToFt(formData.size.widthFeet, formData.size.widthInches);
      const d = exactToFt(formData.size.depthFeet, formData.size.depthInches);
      return w * d;
    }
    return modelPoints
      ? calcPolygonArea([modelPoints.tl, modelPoints.tr, modelPoints.br, modelPoints.bl])
      : 0;
  })();

  const buildingArea = buildingPoints.length === 4
    ? calcPolygonArea(buildingPoints)
    : 0;

  const remainingArea = Math.max(0, plotArea - buildingArea);

  const formatArea = (sqFt) => {
    const rounded = Math.round(sqFt * 100) / 100;
    return { sqFt: rounded };
  };

  const plotAreaFormatted      = formatArea(plotArea);
  const buildingAreaFormatted  = formatArea(buildingArea);
  const remainingAreaFormatted = formatArea(remainingArea);

  const coveragePercent = plotArea > 0
    ? Math.round((buildingArea / plotArea) * 100)
    : 0;

  const currentGaps = calculateGaps();
  const diagonals = calculateDiagonals();
  const diagonal1FeetInches = convertToFeetInches(diagonals.diagonal1);
  const diagonal2FeetInches = convertToFeetInches(diagonals.diagonal2);

  // ── adjustEdge ───────────────────────────────────────────────────────────────
  const adjustEdge = (edgeIndex, direction) => {
    const pts = buildingPointsRef.current;
    if (!pts.length) return;
    const mp = modelPointsRef.current;
    const step = 0.1 * direction;
    const i1 = edgeIndex;
    const i2 = (edgeIndex + 1) % 4;
    const dx = (edgeIndex === 1 || edgeIndex === 3) ? step : 0;
    const dy = (edgeIndex === 0 || edgeIndex === 2) ? step : 0;
    const np1 = { x: pts[i1].x + dx, y: pts[i1].y + dy };
    const np2 = { x: pts[i2].x + dx, y: pts[i2].y + dy };
    if (isPointValidWith(np1, mp) && isPointValidWith(np2, mp)) {
      const newPts = [...pts];
      newPts[i1] = np1;
      newPts[i2] = np2;
      setBuildingPoints(newPts);
    }
  };

  const maximizeBuildingArea = () => {
    if (!modelPoints) return;
    const pts = [
      { x: modelPoints.tl.x, y: modelPoints.tl.y },
      { x: modelPoints.tr.x, y: modelPoints.tr.y },
      { x: modelPoints.br.x, y: modelPoints.br.y },
      { x: modelPoints.bl.x, y: modelPoints.bl.y },
    ];
    setBuildingPoints(pts);
  };

  const resetToSquare = () => {
    if (!bounding) return;
    const cx = (bounding.minX + bounding.maxX) / 2;
    const cy = (bounding.minY + bounding.maxY) / 2;
    const totalW = bounding.maxX - bounding.minX;
    const totalH = bounding.maxY - bounding.minY;
    const setback = Math.max(1.5, Math.min(totalW, totalH) * 0.15);
    const hw = Math.max(1, (totalW / 2) - setback);
    const hh = Math.max(1, (totalH / 2) - setback);
    const sq = [
      { x: cx - hw, y: cy - hh }, { x: cx + hw, y: cy - hh },
      { x: cx + hw, y: cy + hh }, { x: cx - hw, y: cy + hh },
    ];
    const mp = modelPointsRef.current;
    const valid = sq.every((p) => isPointValidWith(p, mp));
    setBuildingPoints(valid ? sq : sq.map((p) => ({
      x: cx + (p.x - cx) * 0.6,
      y: cy + (p.y - cy) * 0.6,
    })));
  };

  // ── NEW: Snap to 90 degrees ───────────────────────────────────────────────────
  // Converts current (possibly skewed) building quad into the nearest axis-aligned
  // rectangle that fits inside the plot, preserving approximate center + size.
  const snapTo90Degrees = () => {
    if (!modelPoints || buildingPoints.length !== 4) return;
    const mp = modelPointsRef.current;
    const pts = buildingPoints;

    // Compute bounding box of current building points
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Snap each value to 0.1 grid
    const snappedMinX = snapToGrid(minX);
    const snappedMaxX = snapToGrid(maxX);
    const snappedMinY = snapToGrid(minY);
    const snappedMaxY = snapToGrid(maxY);

    const rect = [
      { x: snappedMinX, y: snappedMinY }, // top-left (pt 0)
      { x: snappedMaxX, y: snappedMinY }, // top-right (pt 1)
      { x: snappedMaxX, y: snappedMaxY }, // bottom-right (pt 2)
      { x: snappedMinX, y: snappedMaxY }, // bottom-left (pt 3)
    ];

    // Validate all corners fit inside plot; if not, shrink by eps
    let valid = rect.every((p) => isPointValidWith(p, mp));
    if (!valid) {
      // Try nudging inward slightly
      const eps = 0.2;
      const cx = (snappedMinX + snappedMaxX) / 2;
      const cy = (snappedMinY + snappedMaxY) / 2;
      const hw = Math.max(0.5, (snappedMaxX - snappedMinX) / 2 - eps);
      const hh = Math.max(0.5, (snappedMaxY - snappedMinY) / 2 - eps);
      const fallback = [
        { x: cx - hw, y: cy - hh },
        { x: cx + hw, y: cy - hh },
        { x: cx + hw, y: cy + hh },
        { x: cx - hw, y: cy + hh },
      ];
      valid = fallback.every((p) => isPointValidWith(p, mp));
      if (valid) setBuildingPoints(fallback);
      return;
    }

    setBuildingPoints(rect);
  };

  // ── handleAction ─────────────────────────────────────────────────────────────
  const handleAction = () => {
    if (buildingPoints.length === 4 && modelPoints) {
      const gaps = calculateGaps();
      const diags = calculateDiagonals();

      const gapBackLeft = gaps.find((g) => g.label.includes("Back-Left"))?.gap ?? 0;
      const gapBackRight = gaps.find((g) => g.label.includes("Back-Right"))?.gap ?? 0;
      const gapFrontLeft = gaps.find((g) => g.label.includes("Front-Left"))?.gap ?? 0;
      const gapFrontRight = gaps.find((g) => g.label.includes("Front-Right"))?.gap ?? 0;
      const gapMidBack = gaps.find((g) => g.label.includes("Mid-Back"))?.gap ?? 0;
      const gapMidFront = gaps.find((g) => g.label.includes("Mid-Front"))?.gap ?? 0;
      const gapMidLeft = gaps.find((g) => g.label.includes("Mid-Left"))?.gap ?? 0;
      const gapMidRight = gaps.find((g) => g.label.includes("Mid-Right"))?.gap ?? 0;

      const setbacks = {
        front: toFeetDotInches(gapMidFront),
        back: toFeetDotInches(gapMidBack),
        left: toFeetDotInches(gapMidLeft),
        right: toFeetDotInches(gapMidRight),
      };

      const allGaps = {
        backLeft: toFeetDotInches(gapBackLeft),
        backRight: toFeetDotInches(gapBackRight),
        frontLeft: toFeetDotInches(gapFrontLeft),
        frontRight: toFeetDotInches(gapFrontRight),
        midBack: toFeetDotInches(gapMidBack),
        midFront: toFeetDotInches(gapMidFront),
        midLeft: toFeetDotInches(gapMidLeft),
        midRight: toFeetDotInches(gapMidRight),
      };

      const roundedBuildingArea = buildingPoints.map((p) => ({
        x: Math.round(p.x * 10) / 10,
        y: Math.round(p.y * 10) / 10,
      }));

      const updatedData = {
        ...formData,
        buildingArea: roundedBuildingArea,
        setbacks,
        allGaps,
        diagonal1: String(diags.diagonal1),
        diagonal2: String(diags.diagonal2),
        plotAreaSqFt:      String(Math.round(plotArea * 100) / 100),
        buildingAreaSqFt:  String(Math.round(buildingArea * 100) / 100),
        remainingAreaSqFt: String(Math.round(remainingArea * 100) / 100),
        coveragePercent:   String(coveragePercent),
      };

      setFormData(updatedData);

      if (isLastStep) onSubmit(updatedData);
      else onNext(updatedData);
      return;
    }

    if (isLastStep) onSubmit();
    else onNext();
  };

  // ── renderDiagram ─────────────────────────────────────────────────────────────
  const renderDiagram = () => {
    if (!modelPoints || !bounding || buildingPoints.length === 0) {
      return <Text textAlign="center" color="red.500">Loading plot...</Text>;
    }

    const viewW = svgDimensions.width;
    const viewH = svgDimensions.height;
    const bw = bounding.maxX - bounding.minX;
    const bh = bounding.maxY - bounding.minY;

    const padTop = 34;
    const padBottom = 34;
    const padLeft = 48;
    const padRight = 48;

    const availW = viewW - padLeft - padRight;
    const availH = viewH - padTop - padBottom;
    const fs = Math.max(1, Math.min(availW / bw, availH / bh));
    const shiftX = padLeft + (availW - bw * fs) / 2 - bounding.minX * fs;
    const shiftY = padTop + (availH - bh * fs) / 2 - bounding.minY * fs;

    scaleRef.current = { finalScale: fs, shiftX, shiftY };

    const toScreen = (p) => ({ x: p.x * fs + shiftX, y: p.y * fs + shiftY });

    const spp = {
      tl: toScreen(modelPoints.tl), tr: toScreen(modelPoints.tr),
      bl: toScreen(modelPoints.bl), br: toScreen(modelPoints.br),
    };
    const sbp = buildingPoints.map(toScreen);
    const edgeMid = [0, 1, 2, 3].map((i) => ({
      x: (sbp[i].x + sbp[(i + 1) % 4].x) / 2,
      y: (sbp[i].y + sbp[(i + 1) % 4].y) / 2,
    }));

    const plotEdgeDefs = [
      {
        p1: modelPoints.tl, p2: modelPoints.tr,
        label: "Back",
        getLabelPos: (sp1, sp2) => ({
          x: (sp1.x + sp2.x) / 2,
          y: Math.min(sp1.y, sp2.y) - 14,
        }),
        rotation: 0,
      },
      {
        p1: modelPoints.tr, p2: modelPoints.br,
        label: "Right",
        getLabelPos: (sp1, sp2) => ({
          x: Math.max(sp1.x, sp2.x) + 14,
          y: (sp1.y + sp2.y) / 2,
        }),
        rotation: 90,
      },
      {
        p1: modelPoints.br, p2: modelPoints.bl,
        label: "Front (Road)",
        getLabelPos: (sp1, sp2) => ({
          x: (sp1.x + sp2.x) / 2,
          y: Math.max(sp1.y, sp2.y) + 14,
        }),
        rotation: 0,
      },
      {
        p1: modelPoints.bl, p2: modelPoints.tl,
        label: "Left",
        getLabelPos: (sp1, sp2) => ({
          x: Math.min(sp1.x, sp2.x) - 14,
          y: (sp1.y + sp2.y) / 2,
        }),
        rotation: -90,
      },
    ];

    const cornerR = 8;
    const edgeR = 6;

    const getGapLabelOffset = (idx) => {
      const offsets = [
        { dx: -10, dy: -8 },
        { dx: 10, dy: -8 },
        { dx: 10, dy: 8 },
        { dx: -10, dy: 8 },
        { dx: 0, dy: -10 },
        { dx: 12, dy: 0 },
        { dx: 0, dy: 10 },
        { dx: -12, dy: 0 },
      ];
      return offsets[idx] || { dx: 0, dy: 0 };
    };

    return (
      <svg
        ref={svgRef}
        width="100%" height="100%"
        viewBox={`0 0 ${viewW} ${viewH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
      >
        {/* ── Plot boundary ── */}
        <polygon
          points={`${spp.tl.x},${spp.tl.y} ${spp.tr.x},${spp.tr.y} ${spp.br.x},${spp.br.y} ${spp.bl.x},${spp.bl.y}`}
          fill="rgba(229,231,235,0.45)" stroke="#94a3b8" strokeWidth="3" strokeDasharray="6,4"
        />

        {/* ── Plot boundary orientation + size labels ── */}
        {plotEdgeDefs.map((edge, idx) => {
          const dist = calculateDistance(edge.p1, edge.p2);
          const sp1 = toScreen(edge.p1);
          const sp2 = toScreen(edge.p2);
          const raw = edge.getLabelPos(sp1, sp2);

          const lx = Math.max(28, Math.min(viewW - 28, raw.x));
          const ly = Math.max(10, Math.min(viewH - 10, raw.y));

          const txt = `${edge.label}: ${formatDistance(dist)}`;
          const pw = txt.length * 5.2 + 10;
          const tr = edge.rotation ? `rotate(${edge.rotation},${lx},${ly})` : "";

          return (
            <g key={`pe-${idx}`} transform={tr}>
              <rect x={lx - pw / 2} y={ly - 9} width={pw} height={18}
                fill="white" opacity="0.95" rx="4" pointerEvents="none" />
              <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                fontSize="10" fontWeight="700" fill="#1e40af"
                pointerEvents="none" style={{ userSelect: "none" }}>
                {txt}
              </text>
            </g>
          );
        })}

        {/* ── Gap lines + labels ── */}
        {currentGaps.map((gap, idx) => {
          const p1 = toScreen(gap.point);
          const p2 = toScreen(gap.closestPoint);
          const mx = (p1.x + p2.x) / 2;
          const my = (p1.y + p2.y) / 2;
          const off = getGapLabelOffset(idx);
          const lx = mx + off.dx;
          const ly = my + off.dy;
          const txt = formatDistance(gap.gap);
          const pw = txt.length * 6 + 6;

          return (
            <g key={`gl-${idx}`}>
              <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3,3" opacity="0.9" />
              <rect x={lx - pw / 2} y={ly - 7} width={pw} height={14}
                fill="white" opacity="0.9" rx="3" pointerEvents="none" />
              <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                fontSize="9" fill="#374151" fontWeight="700"
                pointerEvents="none" style={{ userSelect: "none" }}>
                {txt}
              </text>
            </g>
          );
        })}

        {/* ── Building diagonals ── */}
        {sbp.length === 4 && (
          <>
            {[[0, 2], [1, 3]].map(([a, b], idx) => {
              const cx2 = (sbp[a].x + sbp[b].x) / 2;
              const cy2 = (sbp[a].y + sbp[b].y) / 2;
              const lx = cx2 + (idx === 0 ? -22 : 22);
              const ly = cy2 + (idx === 0 ? -8 : 8);
              const txt = formatDistance(idx === 0 ? diagonals.diagonal1 : diagonals.diagonal2);
              const pw = txt.length * 6 + 6;
              return (
                <g key={`diag-${idx}`}>
                  <line x1={sbp[a].x} y1={sbp[a].y} x2={sbp[b].x} y2={sbp[b].y}
                    stroke="#6366f1" strokeWidth="1.5" strokeDasharray="5,3" opacity="0.5" />
                  <rect x={lx - pw / 2} y={ly - 7} width={pw} height={14}
                    fill="white" opacity="0.9" rx="3" pointerEvents="none" />
                  <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
                    fontSize="9" fontWeight="700" fill="#4338ca"
                    pointerEvents="none" style={{ userSelect: "none" }}>
                    {txt}
                  </text>
                </g>
              );
            })}
          </>
        )}

        {/* ── Building polygon ── */}
        {sbp.length > 2 && (
          <polygon points={sbp.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="rgba(59,130,246,0.25)" stroke="#3b82f6" strokeWidth="2.5" />
        )}
        {sbp.length > 2 && (
          <text
            x={sbp.reduce((s, p) => s + p.x, 0) / sbp.length}
            y={sbp.reduce((s, p) => s + p.y, 0) / sbp.length}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="12" fontWeight="600" fill="#1e40af"
            pointerEvents="none" style={{ userSelect: "none" }}>
            Building
          </text>
        )}

        {/* ── Edge midpoint controls ── */}
        {edgeMid.map((mid, idx) => {
          const isH = idx === 0 || idx === 2; // top(0) & bottom(2) edges are horizontal

          // ── Outward perpendicular direction (away from building center) ──────
          // buildingCenter in screen coords
          const bcx = sbp.reduce((s, p) => s + p.x, 0) / 4;
          const bcy = sbp.reduce((s, p) => s + p.y, 0) / 4;

          // Unit vector FROM building center TOWARD midpoint = outward normal
          const dxOut = mid.x - bcx;
          const dyOut = mid.y - bcy;
          const dLen = Math.hypot(dxOut, dyOut) || 1;
          const nx = dxOut / dLen; // outward normal x
          const ny = dyOut / dLen; // outward normal y

          // Drag handle sits ON the edge (no offset)
          // +/- buttons: placed outward (outside the building) side-by-side
          const PUSH   = 22; // how far out from midpoint
          const SPREAD = 14; // half-gap between minus and plus along edge

          // Tangent along edge (perpendicular to normal)
          const tx2 = -ny;
          const ty2 =  nx;

          // minus = outward + shifted left-along-edge
          const b1x = mid.x + nx * PUSH - tx2 * SPREAD;
          const b1y = mid.y + ny * PUSH - ty2 * SPREAD;
          // plus  = outward + shifted right-along-edge
          const b2x = mid.x + nx * PUSH + tx2 * SPREAD;
          const b2y = mid.y + ny * PUSH + ty2 * SPREAD;

          // Connecting line from midpoint to center of the two buttons
          const midBtnX = mid.x + nx * PUSH;
          const midBtnY = mid.y + ny * PUSH;

          return (
            <g key={`ec-${idx}`}>
              {/* thin connector line from drag handle to button group */}
              <line
                x1={mid.x} y1={mid.y} x2={midBtnX} y2={midBtnY}
                stroke="#06b6d4" strokeWidth="1" opacity="0.4"
                pointerEvents="none"
              />

              {/* Drag handle — sits on edge midpoint */}
              <circle cx={mid.x} cy={mid.y} r={edgeR}
                fill="#e0f2fe" stroke="#06b6d4" strokeWidth="2.5"
                style={{ cursor: isH ? "ns-resize" : "ew-resize", touchAction: "none" }}
                onMouseDown={(e) => { e.stopPropagation(); onMouseDown(idx, "edge", e); }}
                onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); onMouseDown(idx, "edge", e, true); }} />

              {/* ── Minus button (shrink edge) ── */}
              <g
                onClick={(e) => { e.stopPropagation(); adjustEdge(idx, -1); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); adjustEdge(idx, -1); }}
                style={{ cursor: "pointer" }}
              >
                <circle cx={b1x} cy={b1y} r="14" fill="#f56565" stroke="white" strokeWidth="2" />
                <line x1={b1x - 5} y1={b1y} x2={b1x + 5} y2={b1y}
                  stroke="white" strokeWidth="2.5" pointerEvents="none" />
              </g>

              {/* ── Plus button (expand edge) ── */}
              <g
                onClick={(e) => { e.stopPropagation(); adjustEdge(idx, 1); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); adjustEdge(idx, 1); }}
                style={{ cursor: "pointer" }}
              >
                <circle cx={b2x} cy={b2y} r="14" fill="#48bb78" stroke="white" strokeWidth="2" />
                <line x1={b2x - 5} y1={b2y} x2={b2x + 5} y2={b2y}
                  stroke="white" strokeWidth="2.5" pointerEvents="none" />
                <line x1={b2x} y1={b2y - 5} x2={b2x} y2={b2y + 5}
                  stroke="white" strokeWidth="2.5" pointerEvents="none" />
              </g>
            </g>
          );
        })}

        {/* ── Corner handles ── */}
        {sbp.map((pt, idx) => (
          <g key={`c-${idx}`}>
            <circle cx={pt.x} cy={pt.y} r={cornerR}
              fill="white" stroke="#3b82f6" strokeWidth="3"
              style={{ cursor: "grab", touchAction: "none" }}
              onMouseDown={(e) => { e.stopPropagation(); onMouseDown(idx, "corner", e); }}
              onTouchStart={(e) => { e.stopPropagation(); e.preventDefault(); onMouseDown(idx, "corner", e, true); }} />
            <text x={pt.x} y={pt.y + 16} textAnchor="middle"
              fontSize="10" fontWeight="700" fill="#1d4ed8"
              pointerEvents="none" style={{ userSelect: "none" }}>
              {idx + 1}
            </text>
          </g>
        ))}
      </svg>
    );
  };

  // ── JSX ───────────────────────────────────────────────────────────────────────
  return (
    <Box h="600px" w="100%" maxW="400px" mx="auto" display="flex" flexDirection="column" justifyContent="space-between" px={4} py={6}>
      <Box flex="1" overflowY="auto" overflowX="hidden" pr={2}
        css={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "10px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#a0aec0" },
          "& *": { pointerEvents: viewMode ? "none" : "auto" },
        }}>
        <VStack spacing={4} align="stretch">
          <Text
            fontSize="17px"
            fontWeight="600"
            color="gray.800"
            mb={-2}
            textAlign="center"
          >
            Select your plot Dimensions
          </Text>

          <Text fontSize="13px" color="gray.600" textAlign="center">
            Adjust the building inside your plot to set front, side, and rear setbacks.
          </Text>

          <Box
            ref={containerRef}
            height="360px"
            width="100%"
            bg="white"
            borderRadius="8px"
            p={1}
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {renderDiagram()}
          </Box>

          {/* ── Buttons ── */}
          <Flex gap={2} justifyContent="center" flexWrap="wrap">
            <Button onClick={resetToSquare} size="sm" variant="outline" colorScheme="blue">
              Reset to Square
            </Button>
            <Button
              onClick={snapTo90Degrees}
              size="sm"
              variant="outline"
              colorScheme="orange"
              leftIcon={
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 3v18" />
                </svg>
              }
            >
              Snap 90°
            </Button>
            <Button
              onClick={maximizeBuildingArea}
              size="sm"
              variant="solid"
              colorScheme="blue"
              leftIcon={
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              }
            >
              Maximize Area
            </Button>
          </Flex>

          {/* ── Area Summary ── */}
          <Box borderRadius="14px" overflow="hidden" border="1px solid" borderColor="gray.200" bg="white">

            {/* Header */}
            <Box bg="gray.700" px={4} py={2.5}>
              <Text fontSize="12px" fontWeight="700" color="white" letterSpacing="0.5px">
                📐 AREA SUMMARY
              </Text>
            </Box>

            {/* Row 1 — Total Plot */}
            <Flex
              align="center"
              px={4} py={3}
              borderBottom="1px solid" borderColor="gray.100"
              gap={3}
            >
              <Box
                w="36px" h="28px" flexShrink={0}
                border="2px dashed" borderColor="gray.400"
                bg="gray.100"
                borderRadius="4px"
              />
              <Box flex="1">
                <Text fontSize="11px" color="gray.500" fontWeight="500">Total Plot Area</Text>
                <Text fontSize="10px" color="gray.400">(your full land)</Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="16px" fontWeight="800" color="gray.800" lineHeight="1">
                  {plotAreaFormatted.sqFt}
                </Text>
                <Text fontSize="10px" color="gray.500" fontWeight="500">sq ft</Text>
              </Box>
            </Flex>

            {/* Row 2 — Building footprint */}
            <Flex
              align="center"
              px={4} py={3}
              borderBottom="1px solid" borderColor="blue.100"
              gap={3}
              bg="blue.50"
            >
              <Box
                w="36px" h="28px" flexShrink={0}
                border="2px solid" borderColor="blue.500"
                bg="rgba(59,130,246,0.25)"
                borderRadius="4px"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Text fontSize="8px" fontWeight="700" color="blue.700">🏠</Text>
              </Box>
              <Box flex="1">
                <Text fontSize="11px" color="blue.700" fontWeight="600">Building Area</Text>
                <Text fontSize="10px" color="blue.400">(blue box on diagram)</Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="16px" fontWeight="800" color="blue.700" lineHeight="1">
                  {buildingAreaFormatted.sqFt}
                </Text>
                <Text fontSize="10px" color="blue.500" fontWeight="500">sq ft</Text>
                <Badge colorScheme="blue" fontSize="9px" borderRadius="full" px={2} mt={0.5}>
                  {coveragePercent}% of plot
                </Badge>
              </Box>
            </Flex>

            {/* Row 3 — Open / Remaining */}
            <Flex
              align="center"
              px={4} py={3}
              gap={3}
              bg="green.50"
            >
              <Box
                w="36px" h="28px" flexShrink={0}
                border="2px solid" borderColor="green.400"
                bg="rgba(72,187,120,0.15)"
                borderRadius="4px"
                display="flex" alignItems="center" justifyContent="center"
              >
                <Text fontSize="8px">🌿</Text>
              </Box>
              <Box flex="1">
                <Text fontSize="11px" color="green.700" fontWeight="600">Open / Remaining</Text>
                <Text fontSize="10px" color="green.400">(setbacks + open space)</Text>
              </Box>
              <Box textAlign="right">
                <Text fontSize="16px" fontWeight="800" color="green.700" lineHeight="1">
                  {remainingAreaFormatted.sqFt}
                </Text>
                <Text fontSize="10px" color="green.500" fontWeight="500">sq ft</Text>
                <Badge colorScheme="green" fontSize="9px" borderRadius="full" px={2} mt={0.5}>
                  {100 - coveragePercent}% of plot
                </Badge>
              </Box>
            </Flex>

          </Box>

          {/* ── Setback Gaps — TABLE FORMAT ── */}
          <Box bg="gray.50" borderRadius="10px" overflow="hidden" border="1px solid" borderColor="gray.200">
            <Box bg="gray.100" px={3} py={2} borderBottom="1px solid" borderColor="gray.200">
              <Text fontSize="12px" fontWeight="700" color="gray.700">Setback Gaps at Key Positions</Text>
            </Box>
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontSize="10px" color="gray.600" py={2} pl={3} borderColor="gray.200">Position</Th>
                    <Th fontSize="10px" color="gray.600" py={2} isNumeric borderColor="gray.200">Feet</Th>
                    <Th fontSize="10px" color="gray.600" py={2} isNumeric borderColor="gray.200">Inches</Th>
                    <Th fontSize="10px" color="gray.600" py={2} isNumeric pr={3} borderColor="gray.200">Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentGaps.map((g, idx) => {
                    const { feet, inches } = convertToFeetInches(g.gap);
                    const isCorner = idx < 4;
                    return (
                      <Tr
                        key={g.label}
                        bg={idx % 2 === 0 ? "white" : "gray.50"}
                        _hover={{ bg: "blue.50" }}
                      >
                        <Td fontSize="11px" fontWeight={isCorner ? "500" : "600"} color={isCorner ? "gray.600" : "gray.800"} py={2} pl={3} borderColor="gray.100">
                          <Flex align="center" gap={1.5}>
                            <Box
                              w="6px" h="6px" borderRadius="full" flexShrink={0}
                              bg={isCorner ? "gray.400" : "blue.400"}
                            />
                            {g.label}
                          </Flex>
                        </Td>
                        <Td fontSize="12px" fontWeight="700" color="gray.800" isNumeric py={2} borderColor="gray.100">
                          {feet}
                        </Td>
                        <Td fontSize="12px" fontWeight="700" color="gray.800" isNumeric py={2} borderColor="gray.100">
                          {inches}"
                        </Td>
                        <Td isNumeric py={2} pr={3} borderColor="gray.100">
                          <Badge
                            colorScheme={isCorner ? "gray" : "blue"}
                            fontSize="10px"
                            variant="subtle"
                            borderRadius="full"
                            px={2}
                          >
                            {formatDistance(g.gap)}
                          </Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* ── Diagonals — TABLE FORMAT ── */}
          <Box bg="gray.50" borderRadius="10px" overflow="hidden" border="1px solid" borderColor="gray.200">
            <Box bg="gray.100" px={3} py={2} borderBottom="1px solid" borderColor="gray.200">
              <Text fontSize="12px" fontWeight="700" color="gray.700">Diagonal Measurements</Text>
            </Box>
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="10px" color="gray.600" py={2} pl={3} borderColor="gray.200">Diagonal</Th>
                  <Th fontSize="10px" color="gray.600" py={2} isNumeric borderColor="gray.200">Feet</Th>
                  <Th fontSize="10px" color="gray.600" py={2} isNumeric borderColor="gray.200">Inches</Th>
                  <Th fontSize="10px" color="gray.600" py={2} isNumeric pr={3} borderColor="gray.200">Total</Th>
                </Tr>
              </Thead>
              <Tbody>
                {[
                  { label: "D1 — Corner 1→3", fi: diagonal1FeetInches, val: diagonals.diagonal1 },
                  { label: "D2 — Corner 2→4", fi: diagonal2FeetInches, val: diagonals.diagonal2 },
                ].map(({ label, fi, val }, idx) => (
                  <Tr key={label} bg={idx % 2 === 0 ? "white" : "gray.50"} _hover={{ bg: "purple.50" }}>
                    <Td fontSize="11px" fontWeight="600" color="gray.700" py={2} pl={3} borderColor="gray.100">
                      <Flex align="center" gap={1.5}>
                        <Box w="6px" h="6px" borderRadius="full" bg="purple.400" flexShrink={0} />
                        {label}
                      </Flex>
                    </Td>
                    <Td fontSize="12px" fontWeight="700" color="gray.800" isNumeric py={2} borderColor="gray.100">
                      {fi.feet}
                    </Td>
                    <Td fontSize="12px" fontWeight="700" color="gray.800" isNumeric py={2} borderColor="gray.100">
                      {fi.inches}"
                    </Td>
                    <Td isNumeric py={2} pr={3} borderColor="gray.100">
                      <Badge colorScheme="purple" fontSize="10px" variant="subtle" borderRadius="full" px={2}>
                        {formatDistance(val)}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>

        </VStack>
      </Box>

      <Flex justify="space-between" gap={3} mt={4} flexShrink={0}>
        <Button onClick={onBack} variant="solid" bg="white" color="cyan.600"
          border="1px solid" borderColor="cyan.500" borderRadius="6px" minW="100px" h="40px"
          fontWeight="500" fontSize="14px" _hover={{ bg: "cyan.50", borderColor: "cyan.600" }}>
          ← Previous
        </Button>
        <Button onClick={handleAction} variant="solid" bg="cyan.500" color="white"
          borderRadius="6px" minW="100px" h="40px" fontWeight="500" fontSize="14px" ml="auto"
          _hover={{ bg: "cyan.600" }}>
          {isLastStep ? "Submit" : "Next →"}
        </Button>
      </Flex>
    </Box>
  );
}